import type { ReactNode } from "react";

import styles from "./RoutineChange.module.css";
import {
  type ApiRoutineChange,
  OVERLAP_LABEL,
  type RoutineSnapshot,
} from "../../data/api/routines";
import { cx } from "../../lib/cx";
import { zonedTime } from "../../lib/time";

const TITLE: Record<ApiRoutineChange["action"], string> = {
  create: "Create routine",
  delete: "Delete routine",
  update: "Change routine",
};

type FieldProps = {
  readonly children: ReactNode;
  readonly label: string;
  /** Before above after, for a value too long to sit beside the other. */
  readonly stacked?: boolean;
  /** The value before an edit that changes it. */
  readonly was?: ReactNode;
};

const Field = ({ children, label, stacked = false, was }: FieldProps) => (
  <div
    className={styles.field}
    data-changed={was === undefined ? undefined : ""}
  >
    <dt className={styles.label}>{label}</dt>
    <dd className={cx(styles.value, stacked && styles.stacked)}>
      {was === undefined ? (
        children
      ) : (
        <>
          <del className={styles.was}>{was}</del>
          <span aria-hidden className={styles.arrow}>
            {stacked ? "↓" : "→"}
          </span>
          <ins className={styles.now}>{children}</ins>
        </>
      )}
    </dd>
  </div>
);

const scheduleText = (s: RoutineSnapshot): ReactNode => (
  <>
    {s.schedule_description} · <code className={styles.cron}>{s.schedule}</code>
  </>
);

const promptText = (s: RoutineSnapshot): ReactNode => (
  <pre className={styles.prompt}>{s.prompt}</pre>
);

const yesNo = (s: RoutineSnapshot): string => (s.paused ? "Yes" : "No");

/**
 * What an agent's routine proposal would do, above the Approve / Decline the
 * server asks with (chat.md §Routine approval card). An edit shows the routine
 * as it would be, with each field it moves as before → after.
 */
export const RoutineChange = ({
  change,
}: {
  readonly change: ApiRoutineChange;
}) => {
  const shown = change.after ?? change.before;
  if (!shown) {
    return null;
  }
  // Only an edit has fields beside a before: a create has no before, and a
  // delete changes no field.
  const was = (
    key: keyof RoutineSnapshot,
    render: (s: RoutineSnapshot) => ReactNode,
  ): ReactNode =>
    change.before && key in change.fields ? render(change.before) : undefined;
  const pausedWas = was("paused", yesNo);

  return (
    <section aria-label={TITLE[change.action]} className={styles.change}>
      <div className={styles.title}>{TITLE[change.action]}</div>
      <dl className={styles.fields}>
        <Field label="Name" was={was("name", (s) => s.name)}>
          {shown.name}
        </Field>
        <Field label="Schedule" was={was("schedule", scheduleText)}>
          {scheduleText(shown)}
        </Field>
        <Field label="Timezone" was={was("timezone", (s) => s.timezone)}>
          {shown.timezone}
        </Field>
        {/* A routine being deleted has no next runs worth reading. */}
        {change.action !== "delete" && shown.next_runs.length > 0 ? (
          <Field label="Next runs">
            <ul className={styles.nextRuns}>
              {shown.next_runs.map((at) => (
                <li key={at}>{zonedTime(at, shown.timezone)}</li>
              ))}
            </ul>
          </Field>
        ) : null}
        <Field
          label="Overlap"
          was={was("overlap", (s) => OVERLAP_LABEL[s.overlap])}
        >
          {OVERLAP_LABEL[shown.overlap]}
        </Field>
        {shown.paused || pausedWas !== undefined ? (
          <Field label="Paused" was={pausedWas}>
            {yesNo(shown)}
          </Field>
        ) : null}
        <Field label="Prompt" stacked was={was("prompt", promptText)}>
          {promptText(shown)}
        </Field>
      </dl>
    </section>
  );
};
